// After two hours signed in, ask the user if they're still working; no
// answer within the grace window signs them out. The check-in anchor
// lives in localStorage per user, so page reloads don't restart the
// two-hour clock and confirming in one tab clears the prompt in all of
// them. A laptop that slept through the whole grace window logs out on
// wake — nobody was there to answer.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/useAuth';

const SESSION_CHECK_MS = 2 * 60 * 60 * 1000;   // ask after 2 hours signed in
const RESPONSE_GRACE_MS = 5 * 60 * 1000;       // 5 minutes to answer
const TICK_MS = 30 * 1000;

const anchorKey = (userId) => `sessionCheckIn:${userId}`;

export default function SessionTimeoutGuard() {
  const { user, logout } = useAuth();
  const userId = user?.id || '';
  // While the prompt is open this holds the logout deadline timestamp.
  const [promptDeadline, setPromptDeadline] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const loggingOutRef = useRef(false);

  const readAnchor = useCallback(() => {
    if (!userId) return null;
    try {
      const raw = Number(window.localStorage.getItem(anchorKey(userId)));
      return Number.isFinite(raw) && raw > 0 ? raw : null;
    } catch {
      return null;
    }
  }, [userId]);

  const writeAnchor = useCallback((value) => {
    if (!userId) return;
    try { window.localStorage.setItem(anchorKey(userId), String(value)); } catch { /* private mode */ }
  }, [userId]);

  const forceLogout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try { window.localStorage.removeItem(anchorKey(userId)); } catch { /* private mode */ }
    await logout();
  }, [logout, userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const check = () => {
      const anchor = readAnchor() || Date.now();
      const askAt = anchor + SESSION_CHECK_MS;
      const deadline = askAt + RESPONSE_GRACE_MS;
      const now = Date.now();
      if (now >= deadline) {
        void forceLogout();
        return;
      }
      // Also hides the prompt when another tab confirmed (fresh anchor).
      if (now >= askAt) {
        setPromptDeadline(deadline);
      } else {
        setPromptDeadline(null);
        setSecondsLeft(null);
      }
    };
    if (!readAnchor()) writeAnchor(Date.now());
    const kickoff = window.setTimeout(check, 0);
    const interval = window.setInterval(check, TICK_MS);
    const onStorage = (event) => {
      if (event.key === anchorKey(userId)) check();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.clearTimeout(kickoff);
      window.clearInterval(interval);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId, readAnchor, writeAnchor, forceLogout]);

  // Per-second countdown while the prompt is up.
  useEffect(() => {
    if (!promptDeadline) return undefined;
    const tick = () => {
      const left = Math.max(0, Math.round((promptDeadline - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) void forceLogout();
    };
    const kickoff = window.setTimeout(tick, 0);
    const interval = window.setInterval(tick, 1000);
    return () => {
      window.clearTimeout(kickoff);
      window.clearInterval(interval);
    };
  }, [promptDeadline, forceLogout]);

  const stayWorking = () => {
    writeAnchor(Date.now());
    setPromptDeadline(null);
    setSecondsLeft(null);
  };

  if (!userId || !promptDeadline) return null;
  // Until the first countdown tick lands (a 0ms timeout away), show the
  // full grace window rather than reading the clock during render.
  const left = secondsLeft ?? Math.round(RESPONSE_GRACE_MS / 1000);
  const minutes = Math.floor(left / 60);
  const seconds = String(left % 60).padStart(2, '0');

  return (
    <div
      data-session-timeout-overlay
      role="alertdialog"
      aria-modal="true"
      aria-label="Session check-in"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 6000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
        background: 'rgba(2, 8, 24, 0.68)',
      }}
    >
      <div
        style={{
          width: 'min(430px, 100%)',
          background: '#ffffff',
          border: '1px solid #1e3a8a',
          boxShadow: '0 28px 70px rgba(2, 8, 24, 0.5)',
          color: '#0f172a',
          textAlign: 'center',
          padding: '1.5rem 1.4rem 1.35rem',
        }}
      >
        <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1d4ed8' }}>
          Session check-in
        </div>
        <div style={{ marginTop: 8, fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.15 }}>
          Still working?
        </div>
        <p style={{ margin: '10px 0 0', fontSize: '0.88rem', lineHeight: 1.45, color: '#475569', fontWeight: 500 }}>
          You&rsquo;ve been signed in for a couple of hours. For security, you&rsquo;ll be
          signed out automatically unless you confirm.
        </p>
        <div
          data-session-timeout-countdown
          style={{
            margin: '14px auto 0',
            width: 'fit-content',
            padding: '0.4rem 0.9rem',
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            color: '#92400e',
            fontSize: '1.05rem',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {minutes}:{seconds}
        </div>
        <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            data-session-timeout-stay
            onClick={stayWorking}
            style={{
              minHeight: 44,
              border: 0,
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: '#ffffff',
              fontSize: '0.92rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 10px 24px rgba(29, 78, 216, 0.35)',
            }}
          >
            I&rsquo;m still working
          </button>
          <button
            type="button"
            data-session-timeout-signout
            onClick={() => { void forceLogout(); }}
            style={{
              minHeight: 38,
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#475569',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Sign out now
          </button>
        </div>
      </div>
    </div>
  );
}
